(() => {
  class RingSizeWidget extends HTMLElement {
    constructor() {
      super();
      this._didInit = false;

      this.resultEl = null;
      this.resultSpan = null;
      this.resultDefaultValue = '-';
      this.resultTitleEl = null;
      this.resultTitleDefault = '';

      this.ringSizeSelect = null;
      this.sourceRadios = [];
      this.targetRadios = [];

      this.sourceValues = [];
      this.targetValues = [];

      this._handleSourceRadioChange = this._handleSourceRadioChange.bind(this);
      this._handleTargetRadioChange = this._handleTargetRadioChange.bind(this);
      this._handleSelectChange = this._handleSelectChange.bind(this);
    }

    connectedCallback() {
      if (this._didInit) return;
      this._didInit = true;

      this.resultEl = this.querySelector('[data-ring-size-result]');
      this.resultSpan = this.resultEl?.querySelector('span') || null;
      this.resultDefaultValue = String(this.resultSpan?.textContent || '-').trim() || '-';
      this.resultTitleEl = this.querySelector('[data-ring-size-result-title]');
      this.resultTitleDefault = String(this.resultTitleEl?.dataset?.defaultText || '-').trim() || '-';

      this.ringSizeSelect = this.querySelector('#ring_size');
      this.sourceRadios = Array.from(this.querySelectorAll('input[name="inside_diameter_method"]'));
      this.targetRadios = Array.from(this.querySelectorAll('input[name="convert_diameter_method"]'));

      // Listeners
      this.sourceRadios.forEach((r) => r.addEventListener('change', this._handleSourceRadioChange));
      this.targetRadios.forEach((r) => r.addEventListener('change', this._handleTargetRadioChange));
      this.ringSizeSelect?.addEventListener('change', this._handleSelectChange);

      // Init state from checked radios
      this._applySourceFromCheckedRadio();
      this._applyTargetFromCheckedRadio();
      this._applyConversionResult();
    }

    _parseListFromDataset(raw) {
      const str = String(raw || '').trim();
      if (!str) return [];

      // Preferred delimiter from Liquid: "||"
      if (str.includes('||')) {
        return str
          .split('||')
          .map((s) => String(s).trim())
          .filter(Boolean);
      }

      // Fallback delimiter: ","
      return str
        .split(',')
        .map((s) => String(s).trim())
        .filter(Boolean);
    }

    _normalize(v) {
      return String(v ?? '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, ' ');
    }

    _setResultText(text) {
      if (!this.resultEl || !this.resultSpan) return;
      const next = String(text ?? '').trim();
      this.resultSpan.textContent = next || this.resultDefaultValue || '-';

      this.resultEl.classList.add('animate');
      setTimeout(() => {
        this.resultEl.classList.remove('animate');
      }, 1000);
    }

    _setRingSizeOptions(values) {
      if (!this.ringSizeSelect) return;

      const placeholder = this.ringSizeSelect.querySelector('option') || null;
      this.ringSizeSelect.innerHTML = '';
      if (placeholder) {
        // Ensure placeholder behaves as empty selection
        placeholder.value = placeholder.value || '';
        this.ringSizeSelect.appendChild(placeholder);
      }

      (values || []).forEach((v) => {
        const opt = document.createElement('option');
        opt.value = v;
        opt.textContent = v;
        this.ringSizeSelect.appendChild(opt);
      });

      this.ringSizeSelect.value = '';
      this.ringSizeSelect.disabled = false;
    }

    _applySourceFromCheckedRadio() {
      const checked = this.sourceRadios.find((r) => r && r.checked);
      const values = this._parseListFromDataset(checked?.dataset?.ringSizeValue);
      this.sourceValues = values;
      this._setRingSizeOptions(values);
    }

    _applyTargetFromCheckedRadio() {
      const checked = this.targetRadios.find((r) => r && r.checked);
      const values = this._parseListFromDataset(checked?.dataset?.ringSizeValue);
      this.targetValues = values;
    }

    _setTitle(text) {
      if (!this.resultTitleEl) return;
      this.resultTitleEl.textContent = String(text || '').trim() || this.resultTitleDefault || '';
    }

    _formatTitleFromLabel(labelText) {
      const t = String(labelText || '').trim();
      if (!t) return '';
      return `${t} Ring Size`;
    }

    _getRadioLabelText(radioEl) {
      const el = radioEl || null;
      if (!el?.id) return '';
      const label = this.querySelector(`label[for="${CSS.escape(el.id)}"]`);
      return String(label?.textContent || '').trim();
    }

    _applyTitleFromCheckedTargetRadio() {
      const checked = this.targetRadios.find((r) => r && r.checked);
      const labelText = this._getRadioLabelText(checked);
      this._setTitle(this._formatTitleFromLabel(labelText) || this.resultTitleDefault);
    }

    _applyConversionResult() {
      if (!this.ringSizeSelect) return;

      const selected = String(this.ringSizeSelect.value || '').trim();
      if (!selected) {
        this._setResultText(this.resultDefaultValue);
        return;
      }

      const source = Array.isArray(this.sourceValues) ? this.sourceValues : [];
      const target = Array.isArray(this.targetValues) ? this.targetValues : [];

      if (source.length === 0 || target.length === 0) {
        this._setResultText(this.resultDefaultValue);
        return;
      }

      let idx = source.indexOf(selected);
      if (idx < 0) {
        const normSelected = this._normalize(selected);
        idx = source.findIndex((v) => this._normalize(v) === normSelected);
      }

      const converted = idx >= 0 ? (target[idx] || '') : '';
      this._setResultText(converted || this.resultDefaultValue);
    }

    _handleSourceRadioChange(e) {
      const el = e.currentTarget;
      if (!el || !el.checked) return;
      this.sourceValues = this._parseListFromDataset(el?.dataset?.ringSizeValue);
      this._setRingSizeOptions(this.sourceValues);
      this._applyConversionResult();
    }

    _handleTargetRadioChange(e) {
      const el = e.currentTarget;
      if (!el || !el.checked) return;
      this.targetValues = this._parseListFromDataset(el?.dataset?.ringSizeValue);
      this._setTitle(this._formatTitleFromLabel(this._getRadioLabelText(el)) || this.resultTitleDefault);
      this._applyConversionResult();
    }

    _handleSelectChange() {
      this._applyConversionResult();
    }
  }

  if (!customElements.get('ringsize-widget')) {
    customElements.define('ringsize-widget', RingSizeWidget);
  }
})();